import cookieSetter from "./cookieSetter";
import { BehaviorSubject, Subject } from "rxjs";

class SessionController {
    constructor() {
        const prefix = process.env.REACT_APP_PREFIX;
        const AUTH_TOKEN = (prefix? prefix : '') +  '_token';
        this.AUTH_TOKEN = AUTH_TOKEN;
        const AUTH_EXPIRED = (prefix? prefix : '') +  '_token_expired';
        this.AUTH_EXPIRED = AUTH_EXPIRED;
        this.loggedIn = false;
        this.expiresAt = 0;
        this.timedOut = false;
        this._expiredTimer = null;
        this._inactivityTimer = null;
        this._production = true;

        this._userSubject = new BehaviorSubject();
        this._timeOutSubject = new Subject();
        this._inactivityTimeOutSubject = new Subject();

        this.__LogoutOnExpiredSession = this.__LogoutOnExpiredSession.bind(this);
        this.__OnActivityTimeOut = this.__OnActivityTimeOut.bind(this);
        this.__resetActivityTimer = this.__resetActivityTimer.bind(this);

        // Listener events for user activity
        document.onmousemove = this.__resetActivityTimer;
        document.onkeydown = this.__resetActivityTimer;
        document.onscroll = this.__resetActivityTimer;
        document.onclick = this.__resetActivityTimer;

        this.lastActivity = null;
        const sessionRenewal = process.env.REACT_APP_SESSION_RENEWAL_DEADLINE;
        this.refreshTokenBefore = sessionRenewal? parseInt(sessionRenewal) : 3;

        // Unprotected routes, will not check token storage as fallback
        this.__unprotectedRoutes = [
            '/password',
            '/resetPassword',
            '/activateAccount',
            '/login',
        ]
    }

    get userSubject() {
        return this._userSubject;
        // aggressive timer reset, initialize after subject is assigned
    }

    get timeoutSubject() {
        return this._timeOutSubject;
    }

    __resetActivityTimer() {
        if(this.__unprotectedRoutes.includes(window.location.pathname) || !this.loggedIn){
            return;
        }
        this.lastActivity = Date.now();
    }

    // Session inactivity handlers
    get inactivityTimeOutSubject() {
        return this._inactivityTimeOutSubject;
    }

    setExpiredSession() {
        if(this._production){
            const now = Math.round( Date.now()/1000);
            cookieSetter.setCookie(this.AUTH_EXPIRED, '1', now + 15);
        }else{
            localStorage.setItem(this.AUTH_EXPIRED, '1');
        }
    }

    removeExpiredSession() {
        cookieSetter.deleteCookie(this.AUTH_EXPIRED);
        localStorage.removeItem(this.AUTH_EXPIRED);
    }

    __LogoutOnExpiredSession() {
        this._timeOutSubject.next(true);
    }

    __OnActivityTimeOut() {
        if (!!this.lastActivity) {
            this._inactivityTimeOutSubject.next(Date.now());
        } else {
            this.__LogoutOnExpiredSession();
        }
    }

    get token() {
        if (this._production) {
            return cookieSetter.getCookieValue(this.AUTH_TOKEN);
        } else {
            return localStorage.getItem(this.AUTH_TOKEN);
        }
    }

    get isExpired() {
        if (this._production) {
            return cookieSetter.getCookieValue(this.AUTH_EXPIRED);
        } else {
            return localStorage.getItem(this.AUTH_EXPIRED);
        }
    }

    deleteSession(manual) {
        console.log(`[SESSION CONTROLLER] deleted session`);
        if (typeof manual === 'undefined' && this.token) {
            this.setExpiredSession();
        }
        this.lastActivity = null;
        this.loggedIn = false;
        this._userSubject.next(null);
        this.removeCookie();
        this.clearTimers();
    }

    createSession(token, user, expiresAt){
        if (token) {
            this.setCookie(token, expiresAt);
        }
        this.lastActivity = Date.now();
        this._userSubject.next(user);
        this.loggedIn = true;
        this.initTimers(expiresAt, false);
    }

    currentSession(user, expiresAt) {
        this.createSession(null, user, expiresAt);
    }

    refreshSession(token, expiresAt) {
        this.lastActivity = null;
        this.clearTimers();
        this.setCookie(token, expiresAt);
        this.initTimers(expiresAt, true);
    }

    setCookie(token, expiresAt) {
        if(this._production) {
            const now = Math.round( Date.now()/1000);
            let secondsToLive = expiresAt - now;
            cookieSetter.setCookie(this.AUTH_TOKEN, token, secondsToLive + 15);
        }else{
            localStorage.setItem(this.AUTH_TOKEN, token);
        }
    }

    removeCookie() {
        if (this._production) {
            cookieSetter.deleteCookie(this.AUTH_TOKEN);
        } else {
            localStorage.removeItem(this.AUTH_TOKEN);
        }
    }

    initTimers(expiresAt) {
        this.expiresAt = expiresAt;
        const now = Math.round( Date.now()/1000);
        let expiredIn = this.expiresAt - now;
        if (expiredIn >= this.refreshTokenBefore) {
            expiredIn -= this.refreshTokenBefore;
        }
        const timeout = expiredIn * 1000;
        if (timeout <= 0) {
            this.__OnActivityTimeOut();
        } else {
            this._expiredTimer = setTimeout(this.__OnActivityTimeOut, timeout);
        }
    }

    clearTimers() {
        if (this._inactivityTimer) { clearTimeout(this._inactivityTimer); }
        if (this._expiredTimer) { clearTimeout(this._expiredTimer); }
    }
}

export default (() => {
    const ss = new SessionController();
    return ss;
})();
